const EDITED_PREFIX = 'edited';
const EDITED_PREFIX_WITH_SEPARATOR = `${EDITED_PREFIX}_`;

export const DELIVERY_STATUS_RANK = Object.freeze({
  pending: 0,
  sending: 0,
  ambiguous_timeout: 1,
  server_pending: 1,
  delivery_unknown: 1,
  sent: 1,
  server_ack: 2,
  delivered: 3,
  received: 3,
  read: 4,
  played: 5,
});

export function normalizeStatus(status) {
  if (typeof status !== 'string') return '';
  return status.trim().toLowerCase();
}

export function extractBaseStatus(status) {
  const normalized = normalizeStatus(status);
  if (!normalized) return '';
  if (normalized === EDITED_PREFIX) return '';
  const base = normalized.startsWith(EDITED_PREFIX_WITH_SEPARATOR)
    ? normalized.slice(EDITED_PREFIX_WITH_SEPARATOR.length)
    : normalized;
  return base;
}

export function isEditedStatus(status) {
  const normalized = normalizeStatus(status);
  if (!normalized) return false;
  return normalized === EDITED_PREFIX || normalized.startsWith(EDITED_PREFIX_WITH_SEPARATOR);
}

export function isDeletedStatus(status) {
  const normalized = normalizeStatus(status);
  if (!normalized) return false;
  if (normalized === 'deleted') return true;
  return normalized.startsWith('deleted_');
}

export function getDeliveryStatusRank(status) {
  const base = extractBaseStatus(status);
  return Object.prototype.hasOwnProperty.call(DELIVERY_STATUS_RANK, base)
    ? DELIVERY_STATUS_RANK[base]
    : -1;
}

export function pickMonotonicDeliveryStatus(currentStatus, incomingStatus) {
  const currentRank = getDeliveryStatusRank(currentStatus);
  const incomingRank = getDeliveryStatusRank(incomingStatus);
  if (currentRank < 0) return incomingStatus || currentStatus || '';
  if (incomingRank < 0) return currentStatus || incomingStatus || '';
  return incomingRank >= currentRank ? incomingStatus : currentStatus;
}

const DELETED_NOTE_MARKER = 'mensagem do cliente apagada';
const EDITED_NOTE_MARKER = 'mensagem do cliente editada';

const normalizeMessageBody = (value) =>
  typeof value === 'string' ? value : '';

export function messageIncludesDeletedNote(message) {
  const body = normalizeMessageBody(message);
  if (!body) return false;
  return body.toLowerCase().includes(DELETED_NOTE_MARKER);
}

export function messageIncludesEditedNote(message) {
  const body = normalizeMessageBody(message);
  if (!body) return false;
  return body.toLowerCase().includes(EDITED_NOTE_MARKER);
}

export function stripEditedNoteCurrentSection(message) {
  const body = normalizeMessageBody(message);
  if (!body) return body;
  const lower = body.toLowerCase();
  const editMarkerIndex = lower.indexOf(EDITED_NOTE_MARKER);
  if (editMarkerIndex === -1) return body;
  return body.slice(0, editMarkerIndex).trimEnd();
}

export function buildDisplayableMessageText(message) {
  const raw = normalizeMessageBody(message);
  if (!raw) {
    return {
      combined: '',
      body: '',
      editNote: '',
      editedPrevious: '',
      editedCurrent: '',
      deleteNote: '',
    };
  }

  const lower = raw.toLowerCase();
  const editedIdx = lower.indexOf(EDITED_NOTE_MARKER);
  const deletedIdx = lower.indexOf(DELETED_NOTE_MARKER);

  const noteIndices = [editedIdx, deletedIdx].filter((idx) => idx >= 0);
  const firstNoteIdx = noteIndices.length ? Math.min(...noteIndices) : -1;

  const baseBody =
    firstNoteIdx >= 0 ? raw.slice(0, firstNoteIdx).trim() : raw.trim();

  let editNote = '';
  let editedPrevious = '';
  let editedCurrent = '';
  if (editedIdx >= 0) {
    const editEnd = deletedIdx >= 0 && deletedIdx > editedIdx ? deletedIdx : raw.length;
    editNote = raw.slice(editedIdx, editEnd).trim();
    const normalizedEdit = editNote.replace(/\r/g, '');
    const previousMatch = normalizedEdit.match(/(?:^|\n)Anterior:\s*/i);
    if (previousMatch && previousMatch.index !== undefined) {
      const previousStart = previousMatch.index + previousMatch[0].length;
      const afterPrevious = normalizedEdit.slice(previousStart);
      const currentMatch = afterPrevious.match(/\n\s*Atual:\s*/i);
      if (currentMatch) {
        editedPrevious = afterPrevious.slice(0, currentMatch.index).trim();
        const currentStart = currentMatch.index + currentMatch[0].length;
        editedCurrent = afterPrevious.slice(currentStart).trim();
      } else {
        editedPrevious = afterPrevious.trim();
      }
    }
  }

  const resolvedBody =
    baseBody ||
    (editedCurrent && editedCurrent.trim()) ||
    '';

  let deleteNote = '';
  if (deletedIdx >= 0) {
    let slice = raw.slice(deletedIdx).trim();
    const lowerSlice = slice.toLowerCase();
    const repeatedEditIdx = lowerSlice.indexOf(EDITED_NOTE_MARKER);
    if (repeatedEditIdx > 0) {
      slice = slice.slice(0, repeatedEditIdx).trimEnd();
    }
    deleteNote = slice;
  }

  const parts = [resolvedBody, deleteNote].filter(
    (part) => typeof part === 'string' && part.trim().length > 0
  );

  return {
    combined: parts.join('\n\n').trim(),
    body: resolvedBody,
    editNote,
    editedPrevious,
    editedCurrent,
    deleteNote,
  };
}
